import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { authAPI, containerAssignmentAPI } from '../services/api';
import { hasAtLeastPermission } from '../utils/moduleAccess';
import '../App.css';

const ContainerAssignment = () => {
  const [bagNumber, setBagNumber] = useState('');
  const [containerNumber, setContainerNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingDevice, setCheckingDevice] = useState(false);
  const [device, setDevice] = useState(null);
  
  // Bulk upload states
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [progressId, setProgressId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const { user, setUser } = useAuthStore();
  const permissions = useAuthStore((state) => state.permissions);
  const canEditContainers = user?.user_type === 'operador' || hasAtLeastPermission(permissions, 'container_assignment', 'edit');

  useEffect(() => {
    const restoreUser = async () => {
      if (!user && localStorage.getItem('token')) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
        } catch (err) {
          console.error('Failed to restore user:', err);
        }
      }
    };
    
    restoreUser();
  }, [user, setUser]);

  // Monitor progress stream for bulk uploads
  useEffect(() => {
    if (!progressId) return;

    const eventSource = new EventSource(`/api/container-assignment/progress/${progressId}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setProgressData(data);
        
        if (data.status === 'complete') {
          eventSource.close();
          setProgressId(null);
          setTimeout(() => setShowProgress(false), 1000);
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Progress stream error:', error);
      eventSource.close();
      setProgressId(null);
    };

    return () => eventSource.close();
  }, [progressId]);

  const checkDevice = async () => {
    if (!bagNumber.trim()) {
      toast.error('Please enter a bag number');
      return;
    }

    try {
      setCheckingDevice(true);
      // Call container assignment API to get device info by bag
      const response = await containerAssignmentAPI.getByBag(bagNumber.trim());
      setDevice(response.data);
      
      // If device already has a container number, show warning
      if (response.data.container_number && response.data.container_number.trim()) {
        toast.error(`This seal already has container number: ${response.data.container_number}`);
      } else {
        toast.success('Device found - ready to assign');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Device not found with this bag number');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to check device');
      }
      setDevice(null);
    } finally {
      setCheckingDevice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canEditContainers) {
      toast.error('You do not have permission to assign containers');
      return;
    }

    if (!bagNumber.trim()) {
      toast.error('Bag number is required');
      return;
    }

    if (!containerNumber.trim()) {
      toast.error('Container number is required');
      return;
    }

    // Check if device already has a container number and user is not gods_eye
    if (device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye') {
      toast.error(`Cannot modify: This seal already has container number: ${device.container_number}`);
      return;
    }

    try {
      setLoading(true);
      const response = await containerAssignmentAPI.create({
        bag: bagNumber,
        container_number: containerNumber,
      });

      toast.success(response.data.message);
      
      // Reset form
      setBagNumber('');
      setContainerNumber('');
      setDevice(null);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('This seal already has a container number. Only administrators can modify.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to assign container');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();

    if (!canEditContainers) {
      toast.error('You do not have permission to bulk-assign containers');
      return;
    }

    if (!bulkFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setBulkLoading(true);
      setShowProgress(true);

      const formData = new FormData();
      formData.append('file', bulkFile);

      const response = await containerAssignmentAPI.bulkAssign(formData);
      
      setProgressId(response.data.operation_id);
      setBulkResults({
        assigned: response.data.assigned_devices?.length || 0,
        errors: response.data.error_count || 0,
        total: (response.data.assigned_devices?.length || 0) + (response.data.error_count || 0),
      });

      toast.success(`Bulk upload started - ${response.data.assigned_devices?.length || 0} containers to assign`);
      
      // Reset file input
      setBulkFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setShowProgress(false);
      console.error('Error uploading file:', err);
      toast.error(err.response?.data?.detail || 'Failed to start bulk upload');
    } finally {
      setBulkLoading(false);
    }
  };

  // Check if user is operador and has edit permission for container_assignment
  if (user && user.user_type !== 'operador') {
    return (
      <div className="dashboard-container">
        <h1>Container Assignment</h1>
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #fca5a5',
          color: '#991b1b',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px',
        }}>
          ❌ This module is only available for operadores
        </div>
      </div>
    );
  }

  if (user && user.user_type === 'operador' && !canEditContainers) {
    return (
      <div className="dashboard-container">
        <h1>Container Assignment</h1>
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          color: '#92400e',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px',
        }}>
          ⚠ You have view-only access to container assignments. Assigning containers is disabled.
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1>📦 Assign Container Numbers</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Assign container numbers to seals in your depot. Enter the bag number to find the seal.
      </p>

      <div style={{
        maxWidth: '400px',
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        marginBottom: '30px',
      }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
              🏷️ Bag Number *
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                value={bagNumber}
                onChange={(e) => setBagNumber(e.target.value)}
                placeholder="e.g., BAG123"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={checkDevice}
                disabled={loading || checkingDevice || !bagNumber.trim() || !canEditContainers}
                style={{
                  padding: '10px 16px',
                  backgroundColor: checkingDevice ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading || checkingDevice || !bagNumber.trim() ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {checkingDevice ? '⏳' : '✓'} Check
              </button>
            </div>
          </div>

          {device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye' && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '2px solid #fca5a5',
              color: '#991b1b',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
              fontWeight: '500',
            }}>
              🔒 This seal already has container number: <strong>{device.container_number}</strong>
              <br />
              Only administrators can modify existing container assignments.
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
              📦 Container Number *
            </label>
            <input
              type="text"
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value)}
              placeholder="e.g., CONTAINER_001"
              disabled={
                loading ||
                !canEditContainers ||
                (device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye')
              }
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={
              loading ||
              !canEditContainers ||
              (device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye')
            }
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading || (device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye') ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading || (device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye') ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '⏳ Assigning...' : device?.container_number && device.container_number.trim() && user?.user_type !== 'gods_eye' ? '🔒 Locked' : '✓ Assign Container'}
          </button>
        </form>
      </div>

      {/* Bulk Upload Section */}
      <div style={{
        maxWidth: '600px',
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        marginBottom: '30px',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
          📤 Bulk Upload Containers
        </h2>
        <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
          Upload a CSV or Excel file to assign multiple containers at once.
          <br />
          Required columns: <strong>bag</strong>, <strong>container_number</strong>
        </p>

        <form onSubmit={handleBulkUpload}>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
              📁 Select File (CSV or Excel)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              disabled={bulkLoading}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                boxSizing: 'border-box',
              }}
            />
            <small style={{ color: '#999', display: 'block', marginTop: '6px' }}>
              Max file size: 10 MB. Formats: .csv, .xlsx, .xls
            </small>
          </div>

          <button
            type="submit"
            disabled={bulkLoading || !bulkFile || !canEditContainers}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: bulkLoading || !bulkFile ? '#9ca3af' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: bulkLoading || !bulkFile ? 'not-allowed' : 'pointer',
            }}
          >
            {bulkLoading ? '⏳ Processing...' : bulkFile ? `📤 Upload: ${bulkFile.name}` : '📁 Select File First'}
          </button>
        </form>
      </div>

      {/* Progress Bar */}
      {showProgress && progressData && (
        <div className="progress-container" style={{ maxWidth: '600px', marginBottom: '30px' }}>
          <div className="progress-header">
            <h4 style={{ margin: 0 }}>Bulk Assignment Progress</h4>
            <span className="progress-status">
              {progressData.status === 'complete' ? '✓ Complete' : 'In Progress...'}
            </span>
          </div>
          <div className="progress-info">
            <span>{progressData.current} / {progressData.total} containers</span>
            <span className="progress-percentage">{progressData.percentage}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${progressData.percentage}%`,
                backgroundColor: progressData.status === 'error' ? '#dc3545' : '#8b5cf6'
              }}
            ></div>
          </div>
          {progressData.message && (
            <div className="progress-message">
              {progressData.message}
            </div>
          )}
          {bulkResults && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
              <strong>Results:</strong> {bulkResults.assigned} assigned
              {bulkResults.errors > 0 ? `, ${bulkResults.errors} errors` : ''}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '4px', fontSize: '12px', color: '#004085' }}>
        <strong>👤 Role:</strong> {user?.user_type || 'Unknown'} | <strong>🏢 Depot:</strong> {user?.depot_id || 'Not assigned'}
      </div>
    </div>
  );
};

export default ContainerAssignment;
